import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAtom } from 'jotai';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { StudyLocalizationStatus } from '@axon/rosetta-sdk';
import { FirebaseAppContext } from 'libs.firebase_react';
import { useLocalization, useLocalizationExport, useLocalizationResource } from 'libs.localization.react';
import { Locale, Text, useLocalizeMessage } from 'libs.nucleus';
import { DeviceSelector, selectedDeviceAtom } from 'libs.preview';
import { PageHeader } from 'libs.react';
import { StudyLocaleStatusProgress, TranslationsOutOfSyncBanner } from '../../components';
import { AIGeneratedBanner } from '../../components/ai_generated_banner/AI_generated_banner.component';
import { AssetsInfoPanel } from '../../components/assets_info_panel';
import { AssetReviewScreenList } from '../../components/review_screenlist/asset_review_screenlist.component';
import { TranslationWorkbenchContext } from '../../contexts';
import { useLocalizationExportV2 } from '../../hooks';
import { deviceList as defaultDeviceList } from '../../utils/device_list';
import { getDevicelistForAssetType } from '../../utils/device_utils';
import { isTranslationV2Enabled } from '../../utils/env_utils';
import { toSentenceCase } from '../../utils/string_utils';
export const ReviewView = ({ assetId, studyId }) => {
    const t = useLocalizeMessage();
    const [selectedDevice] = useAtom(selectedDeviceAtom);
    const { locale = Locale.en_US } = useParams();
    const [queryParams] = useSearchParams();
    const assetType = queryParams.get('assetType');
    const { logEvent } = useContext(FirebaseAppContext);
    const { exportLocalization, isFetching } = useLocalizationExport();
    const { mutate: exportLocalizationV2, isPending: isExportingV2 } = useLocalizationExportV2();
    const { studyLocalizationStatus, resourcesAssets, studyAssets, notificationsAssets, study } = useContext(TranslationWorkbenchContext);
    const isStudyinDraft = studyLocalizationStatus !== StudyLocalizationStatus.READY;
    const productVersion = queryParams.get('productVersion') || study.productTemplate;
    const asset = {
        id: assetId,
        type: assetType,
        version: '1',
    };
    useEffect(() => {
        logEvent('translations_screen_review_device_size', {
            studyId,
            assetId,
            ...selectedDevice,
        });
    }, [selectedDevice]);
    const { localizationEntry, localization } = useLocalization(asset);
    const { version: localeVersion, status: localeStatus, localization_origin: localizationOrigin, } = localizationEntry?.supported_locales?.[locale] || {};
    const { localizationResourceEntry } = useLocalizationResource(asset, locale, localeVersion);
    const currentBaseLocaleVersion = localizationEntry?.supported_locales?.[localizationEntry?.base_locale || Locale.en_US]?.version;
    const currentResourceBaseLocaleVersion = localizationResourceEntry?.base_locale_version;
    const isBaseLocaleInSync = currentBaseLocaleVersion === currentResourceBaseLocaleVersion;
    const allAssets = { ...notificationsAssets, ...resourcesAssets, ...studyAssets };
    const twId = localization?.getId();
    const currentAsset = twId ? allAssets[twId] : undefined;
    const pageTitle = currentAsset?.displayName || toSentenceCase(assetType);
    const breadcrumbPages = useMemo(() => [
        { name: t('Translations'), path: `/studies/${studyId}/translations` },
        {
            name: pageTitle,
            path: `/studies/${studyId}/translations/${assetId}?assetType=${assetType}`,
        },
        { name: locale || '', path: `/studies/${studyId}/translations/${assetId}/review/${locale}` },
    ], [studyId, assetId, pageTitle]);
    const handleExport = useCallback(() => {
        if (asset && localization) {
            const viewport = Object.entries(defaultDeviceList).find(([, value]) => value.device === selectedDevice.device)?.[0];
            exportLocalization({
                resources: ['screens'],
                locales: [locale],
                ...(viewport && { viewport }),
                assets: { [localization.getId()]: [`${locale}~${localeVersion}`] },
            });
            logEvent('translations_screen_report_downloaded', {
                context: 'study',
                type: assetType,
                id: assetId,
                locale,
            });
        }
    }, [asset, locale, selectedDevice, localeVersion, localization]);
    const handleExportV2 = useCallback(() => {
        if (asset && localization) {
            const viewport = Object.entries(defaultDeviceList).find(([, value]) => value.device === selectedDevice.device)?.[0];
            exportLocalizationV2({
                resources: ['screens'],
                ...(viewport && { viewport }),
                assets: { [localization.getId()]: [`${locale}~${localeVersion}`] },
                productVersion,
            });
            logEvent('translations_screen_report_downloaded', {
                context: 'study',
                type: assetType,
                id: assetId,
                locale,
                serviceVersion: 'v2',
            });
        }
    }, [asset, localization, locale, localeVersion]);
    const headerOptions = [];
    if (isTranslationV2Enabled()) {
        headerOptions.push({
            name: t('Export'),
            onClick: handleExportV2,
            variant: 'primary',
            disabled: isStudyinDraft || isExportingV2,
            isLoading: isExportingV2,
        });
    }
    else {
        headerOptions.push({
            name: t('Export'),
            onClick: handleExport,
            variant: 'primary',
            disabled: isStudyinDraft || isFetching,
            isLoading: isFetching,
        });
    }
    // @TODO Consume this info from firebase
    const info = [{ title: t('Name'), description: pageTitle }];
    const hasResource = localizationEntry.localization_resources && !!Object.keys(localizationEntry.localization_resources).length;
    const previewOptions = localizationEntry.metadata?.preview;
    const deviceList = getDevicelistForAssetType(assetType);
    const defaultDevice = previewOptions?.default_viewport && deviceList[previewOptions?.default_viewport]
        ? deviceList[previewOptions?.default_viewport]
        : deviceList[Object.keys(deviceList)[0]];
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: locale, breadcrumbPages: breadcrumbPages, options: headerOptions, LinkComponent: Link, customOptions: locale !== Locale.en_US && asset ? (_jsx(StudyLocaleStatusProgress, { asset: asset, locale: locale, assetId: assetId, isDisabled: isStudyinDraft || !isBaseLocaleInSync, disabledMessage: !isBaseLocaleInSync ? t('Translations are out of sync with the source') : '', trackingContext: { context: 'study', type: assetType } })) : (_jsx("span", {})) }), _jsx(AIGeneratedBanner, { status: localeStatus, origin: localizationOrigin }), !isBaseLocaleInSync && (_jsx("div", { className: 'mb-6', children: _jsx(TranslationsOutOfSyncBanner, {}) })), _jsxs("div", { className: 'flex flex-col gap-6', children: [_jsx(AssetsInfoPanel, { listItems: info, locale: locale, title: t('Information'), version: localeVersion, localizationVersion: {
                            status: localeStatus,
                            version: Number(localeVersion),
                            origin: localizationOrigin,
                        } }), _jsx("hr", {}), _jsxs("div", { className: 'flex flex-col gap-2', children: [_jsx(Text, { size: 'sm', children: t('Device size') }), defaultDevice && (_jsx(DeviceSelector, { devices: deviceList, defaultDevice: defaultDevice, showDimensions: true, width: 'lg' }))] }), hasResource && _jsx(AssetReviewScreenList, { localizationEntry: localizationEntry, productVersion: productVersion })] })] }));
};
